import { useEffect } from "react";

import { Nav, Footer } from "../components/Nav";
import Cover from "../images/HoneyBuddy/HoneyBuddy Cover.png";
import wireframe from "../images/HoneyBuddy/HoneyBuddy Wireframes.png";
import initialColor from "../images/HoneyBuddy/Logo and Starting Colors.png";
import brainstorm from "../images/HoneyBuddy/HoneyBuddy Brainstorm.png";
import sytleGuide from "../images/HoneyBuddy/HoneyBuddy Style Guide.png";
import onboardingGif from "../images/HoneyBuddy/HB Onboarding.gif";
import preferenceImg from "../images/HoneyBuddy/HB Preferences.png";
import loginImg from "../images/HoneyBuddy/HB Login.png";
import goalsPage from "../images/HoneyBuddy/HB Goals.png";
import challengePage from "../images/HoneyBuddy/HB Challenge.png";
import goalsGif from "../images/HoneyBuddy/HB Goals Gif.gif";
import chatbotGif from "../images/HoneyBuddy/HB Chatbot Gif.gif";
import notificationGif from "../images/HoneyBuddy/HB Widget and Notification.gif";
import widgetImg from "../images/HoneyBuddy/HB Widget.png";
import notificationImg from "../images/HoneyBuddy/HB Notification.png";
import finalImg from "../images/HoneyBuddy/HoneyBuddy Final.png";
import {
  ProjectInfo,
  BigStatement,
  TwoColumns,
  TextSection,
  MoreSection,
} from "../components/ProjectDetails";

export default function HoneyBuddy() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <>
      <header id="project-cover">
        <Nav color="white" />

        <h1>HoneyBuddy</h1>
        <p>
          Awarded Best User Interface at the 2025 Rice Design-a-Thon.
          <br />
          Mobile habit and goal app for busy college students.
        </p>
        <img id="cover" src={Cover} alt="HoneyBuddy Mockup" />
      </header>

      <main id="project-detail-main">
        <ProjectInfo
          role={["UI/UX Designer", "UX Researcher"]}
          team={["3 Members", "Ashley Cha", "Hayoung Ryow"]}
          toolkit={["Figma", "Miro", "Adobe Illustrator"]}
          duration="2 Days (2025)"
        />
        <BigStatement header="Big Question">
          How can we help busy college students maintain a healthy lifestyle and
          achieve personal goals?
        </BigStatement>
        <TextSection type="callout" header="Solution" title="HoneyBuddy">
          <p>
            A mobile app and widget that helps college students improve their
            lifestyle and reach personal goals with AI, location tracking, and
            community challenges.
          </p>
          <a
            className="primary-button"
            href="https://www.figma.com/proto/GwkI3HEFFJcR1iOkW4oYEX/HoneyBuddy-(Mandy-Version)?page-id=3%3A2&node-id=183-1436&p=f&viewport=100%2C-364%2C0.32&t=7fAKYNoThLvFvntA-1&scaling=scale-down&content-scaling=fixed&starting-point-node-id=183%3A1436"
            target="_blank"
            rel="noopener noreferrer"
          >
            Figma Prototype
          </a>
          <h1>My Key Contributions</h1>
          <ul>
            <li>Ideated the initial concept and defined key features.</li>
            <li>
              Developed interview and survey questions in collaboration with the
              team.
            </li>
            <li>Conducted 4 out of 11 one-on-one user interviews.</li>
            <li>
              Designed the final key interfaces, including the Challenge Page,
              Personal Goals Page, Chatbot Page, and Login/Sign Up Page.
            </li>
            <li>Prototyped over 70% of the project.</li>
            <li>Created the Style Guide to ensure design consistency.</li>
          </ul>
        </TextSection>
        <TextSection
          type="normal-text"
          header="Brainstorming"
          title="Generating Research Questions & Narrowing Down Scope"
        >
          <img src={brainstorm} alt="HoneyBuddy Brainstorming in Miro"></img>
        </TextSection>
        <TextSection
          type="normal-text"
          header="User Research & Insights"
          title=" Understanding College Students’ Lifestyle Challenges"
        >
          <p>
            We conducted 11 one-on-one user interviews and received 47 survey
            responses from students to better understand their challenges in
            building sustainable lifestyle habits.
          </p>
          <p>
            <strong>Our key findings:</strong>
          </p>
          <ul>
            <li>90% of college students wanted to improve their lifestyle</li>
            <li>
              The majority of students surveyed described their diets as
              “unhealthy”
            </li>
            <li>
              70% of students have their phones within arm’s reach around
              80-100% of the time
            </li>
            <li>
              80.9% of students self-reported their stress levels being between
              6-10 (1 being none and 1 being the most)
            </li>
            <br />
            <li>
              Most prevalent improvements were eating healthy and exercising
            </li>
            <li>
              Biggest hindering factors were lack of time due to busy college
              schedules and lack of motivation
            </li>
          </ul>
        </TextSection>
        <TextSection
          type="normal-text"
          header="ideation"
          title="Turning Insights Into Features"
        >
          <TwoColumns
            leftContent={[
              "An App and Widget",
              "Weekly Challenges",
              "AI Chatbot to Create New Goals",
              "Personalized Reminders",
              "Badges and Streaks as Motivations",
            ]}
            rightContent={[
              "We created an app and widget because 70% of students have their phone within arm’s reach 80-100% of the time. This makes mobile solutions the most effective for effortless goal reminders, with a widget serving as a visual cue each time students use their phone.",
              "We included weekly challenges shared app-wide because our survey and interview findings show that most students have similar lifestyle goals, like eating healthy and exercising more. These community-based challenges provide external motivation and encourage peer discussions.",
              "We chose an AI chatbot over the traditional form-style method to help students set goals quickly and reduce friction. Its flexibility allows for unique, personalized goals and more tailored recommendations.",
              "Since students often feel stressed and overwhelmed, we incorporated tailored notifications throughout the day to remind them of their lifestyle goals. These notifications help students stay on track effortlessly by removing the need for active planning or thought.",
              "We incorporated badges and streaks based on our UX research, where interviewees expressed a desire for gamification. This approach makes goal achievement more engaging and enjoyable, encouraging students to stay motivated.",
            ]}
          ></TwoColumns>
        </TextSection>
        <TextSection
          type="normal-text"
          header="planning"
          title="Logo and Starting Colors"
        >
          <p>
            We chose yellows and oranges to reflect the colors of a honeycomb
            while also conveying a fun, friendly, and positive mood. The hive
            and honeycomb theme symbolizes community and togetherness,
            reinforcing our goal of bringing people together.
          </p>
          <img src={initialColor} alt="HoneyBuddy Initial Colors"></img>
        </TextSection>
        <TextSection type="normal-text" header="ideation" title="Wireframing">
          <img src={wireframe} alt="HoneyBuddy Wireframe"></img>
        </TextSection>
        <TextSection
          type="callout"
          header="Style Guide"
          title="Honeycomb-Inspired Elements and Expanded Colors"
        >
          <img src={sytleGuide} alt="HoneyBuddy Final Style Guide"></img>

          <ul>
            <li>
              We expanded the initial color palette while maintaining the
              dominant yellow and orange hues to evoke a sense of joy,
              positivity, and excitement—the emotions we want college students
              to experience while using the app.
            </li>
            <li>
              We also incorporated honeycomb-inspired elements into our designs
              to create a visually cohesive app that reinforces its emphasis on
              community and interconnectedness.
            </li>
            <li>
              Lastly, we chose a sans-serif font to uphold a clean, modern, and
              minimal aesthetic.
            </li>
          </ul>
        </TextSection>
        <TextSection type="callout" header="Prototype" title="Try  HoneyBuddy">
          <a
            className="primary-button"
            href="https://www.figma.com/proto/GwkI3HEFFJcR1iOkW4oYEX/HoneyBuddy-(Mandy-Version)?page-id=3%3A2&node-id=183-1436&p=f&viewport=100%2C-364%2C0.32&t=7fAKYNoThLvFvntA-1&scaling=scale-down&content-scaling=fixed&starting-point-node-id=183%3A1436"
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginTop: "1rem" }}
          >
            Figma Prototype
          </a>
        </TextSection>
        <TextSection type="callout" header="Final Design" title="Onboarding">
          <p>
            User onboarding should be efficient while collecting essential
            information to personalize the application. Our app gathers basic
            details about the types of goals users want to achieve, along with
            their notification and location tracking preferences.
          </p>
          <div className="three-img">
            <img src={loginImg} alt="HoneyBuddy Login Page"></img>
            <img src={onboardingGif} alt="HoneyBuddy Onboarding Gif"></img>
            <img src={preferenceImg} alt="HoneyBuddy Preference Page"></img>
          </div>
        </TextSection>

        <TextSection
          type="callout"
          header="Final Design"
          title="Weekly Challenges & Personal Goals"
        >
          <p>
            Weekly challenges are designed to inspire and motivate students to
            achieve a healthy lifestyle, focusing on common challenges faced by
            college students. The uniquely designed badges serve as rewards,
            encouraging continued engagement and progress. This also serves as
            the default page users see upon opening the app, providing a
            constant visual reminder.
          </p>

          <div className="three-img">
            <img
              src={challengePage}
              alt="HoneyBuddy Weekly Challenges Page"
            ></img>
            <img src={goalsGif} alt="HoneyBuddy Challenge and Goals Gif"></img>
            <img src={goalsPage} alt="HoneyBuddy Goals Page"></img>
          </div>
          <p>
            The Goals page allows users to create personal goals, each fitting
            into a slot within the honeycomb. As users add more goals, their
            hive expands, symbolizing how small habits accumulate into
            meaningful achievements.
          </p>
        </TextSection>

        <TextSection type="callout" header="Final Design" title="AI Chatbot">
          <div className="sidebyside">
            <img src={chatbotGif} alt="HoneyBuddy Chatbot Gif"></img>
            <p>
              Using an AI chatbot instead of a traditional form-style
              goal-setting approach offers greater flexibility. Users can share
              the goals they want to achieve, and the chatbot provides
              personalized suggestions on how to accomplish them based on their
              current habits.
            </p>
          </div>
        </TextSection>

        <TextSection
          type="callout"
          header="Final Design"
          title="Widget & Notification"
        >
          <p>
            To accommodate college students' busy schedules, we aim to create
            the most effortless way to achieve goals. This is achieved through a
            home screen widget that delivers visual reminders and notifications
            that provide periodic encouragement to help users stay on track with
            their goals.
          </p>
          <div className="three-img">
            <img src={widgetImg} alt="HoneyBuddy Widget Page"></img>
            <img
              src={notificationGif}
              alt="HoneyBuddy Notification and Widget Gif"
            ></img>
            <img src={notificationImg} alt="HoneyBuddy Notifcation "></img>
          </div>
          <p>
            Additionally, the app leverages data such as screen time and motion
            to offer lifestyle suggestions through notifications, like taking a
            walk or putting the phone down for a break.
          </p>
        </TextSection>

        <TextSection
          type="normal-text"
          header="Final Design"
          title="All Design Pages"
        >
          <img
            src={finalImg}
            className="full-width-img"
            alt="Final HoneyBuddy Designs"
          />
        </TextSection>
      </main>
      <MoreSection exclude={["honeybuddy"]} />

      <Footer />
    </>
  );
}
